import { Box, Typography, useMediaQuery } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// Mock logos (replace later)
const vogueLogo =
  "https://logos-world.net/wp-content/uploads/2021/11/Vogue-Logo-700x394.png";
const nytLogo =
  "https://logos-world.net/wp-content/uploads/2020/11/The-New-York-Times-Logo-700x394.png";
const usWeeklyLogo =
  "https://www.logo.wine/a/logo/Entertainment_Weekly/Entertainment_Weekly-Logo.wine.svg";
const smpLogo =
  "https://www.logo.wine/a/logo/South_China_Morning_Post/South_China_Morning_Post-Logo.wine.svg";
const betLogo = "https://www.logo.wine/a/logo/BET/BET-Logo.wine.svg";
const entOne =
  "https://www.logo.wine/a/logo/Entertainment_One/Entertainment_One-Logo.wine.svg";
const bbcLogo =
  "https://www.logo.wine/a/logo/BBC_World_Service/BBC_World_Service-Logo.wine.svg";
const warnerLogo =
  "https://www.logo.wine/a/logo/WarnerMedia/WarnerMedia-Logo.wine.svg";
const newYorkerLogo =
  "https://www.logo.wine/a/logo/The_New_Yorker/The_New_Yorker-Logo.wine.svg";
const ticLogo =
  "https://www.logo.wine/a/logo/Ticketmaster/Ticketmaster-Logo.wine.svg";

const LOGOS = {
  vogue: vogueLogo,
  nyt: nytLogo,
  bet: betLogo,
  usWeekly: usWeeklyLogo,
  southChinaMorningPost: smpLogo,
  entertainmentOne: entOne,
  bbcWorldService: bbcLogo,
  warnerMedia: warnerLogo,
  theNewYorker: newYorkerLogo,
  ticketMaster: ticLogo,
};

export default function PressPraiseSection() {
  const { t } = useTranslation("dancingstarsPress");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const quotes = t("quotes", { returnObjects: true });

  // Reduced desktop cards
  const itemsPerView = isDesktop ? 2 : 1;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + itemsPerView) % quotes.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [quotes.length, itemsPerView]);

  const visibleQuotes = quotes.slice(index, index + itemsPerView);

  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        px: { xs: 3, md: 10 },
      }}
    >
      {/* Section Label */}
      <Typography
        sx={{
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          opacity: 0.55,
          mb: 6,
          fontSize: "0.75rem",
        }}
      >
        {/* {t("sectionLabel")} */}
      </Typography>

      <AnimatePresence mode="wait">
        <Box
          component={motion.div}
          key={index}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -24 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
            },
            gap: { xs: 4, md: 6 },
          }}
        >
          {visibleQuotes.map((item) => (
            <Box
              key={item.id}
              sx={{
                borderRadius: "20px",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                border: "1px solid rgba(255,255,255,0.08)",
                minHeight: 240,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Quote */}
              <Box sx={{ p: { xs: 4, md: 5 }, flexGrow: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Playfair Display",
                    fontSize: { xs: "1.05rem", md: "1.15rem" },
                    lineHeight: 1.65,
                    opacity: 0.95,
                  }}
                >
                  “{item.quote}”
                </Typography>
              </Box>

              {/* Footer — LIGHT PANEL */}
              <Box
                sx={{
                  px: { xs: 4, md: 5 },
                  py: 3,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.82), rgba(245,245,245,0.4))",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {/* Logo */}
                <Box
                  component="img"
                  src={LOGOS[item.id]}
                  alt={item.name}
                  sx={{
                    height: 36,
                    maxWidth: 120,
                    objectFit: "contain",
                  }}
                />

                {/* Name */}
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#111",
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </AnimatePresence>
    </Box>
  );
}
